/**
 * Structured logging for the browser.
 *
 * Everything goes out as a single JSON line so it can be shipped to a log sink
 * later without reparsing. Never log a phone number, a token or an API key —
 * `redact()` strips the fields we know are sensitive, and anything you add to
 * SENSITIVE_KEYS is stripped from nested objects too.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

const ORDER: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 }

/**
 * Compared against `key.toLowerCase()`, so every entry here must already be
 * lowercase. A camelCase entry such as `apiKey` would silently never match,
 * which is exactly the leak the unit test guards against.
 */
const SENSITIVE_KEYS = [
  'password', 'pass', 'passphrase',
  'token', 'idtoken', 'accesstoken', 'refreshtoken', 'bearer',
  'apikey', 'api_key', 'secret', 'clientsecret', 'privatekey',
  'authorization', 'cookie', 'session',
  'totp', 'otp', 'code', 'seed', 'pepper',
  'mobile', 'phone', 'email',
]

function configuredLevel(): LogLevel {
  const raw = (import.meta.env?.VITE_LOG_LEVEL as string | undefined) ?? 'info'
  return (['debug', 'info', 'warn', 'error'] as const).includes(raw as LogLevel)
    ? (raw as LogLevel)
    : 'info'
}

export function redact(value: unknown, depth = 0): unknown {
  if (depth > 6) return '[depth]'
  if (value === null || value === undefined) return value
  if (Array.isArray(value)) return value.map((v) => redact(v, depth + 1))
  if (value instanceof Error) return { name: value.name, message: value.message }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = SENSITIVE_KEYS.includes(k.toLowerCase()) ? '[redacted]' : redact(v, depth + 1)
    }
    return out
  }
  return value
}

export interface LogRecord {
  level: LogLevel
  msg: string
  time: string
  ctx?: Record<string, unknown>
}

export function buildRecord(level: LogLevel, msg: string, ctx?: Record<string, unknown>): LogRecord {
  const rec: LogRecord = { level, msg, time: new Date().toISOString() }
  if (ctx && Object.keys(ctx).length) rec.ctx = redact(ctx) as Record<string, unknown>
  return rec
}

function emit(level: LogLevel, msg: string, ctx?: Record<string, unknown>): void {
  if (ORDER[level] < ORDER[configuredLevel()]) return
  const line = JSON.stringify(buildRecord(level, msg, ctx))
  const fn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log
  fn(line)
}

export const log = {
  debug: (msg: string, ctx?: Record<string, unknown>) => emit('debug', msg, ctx),
  info: (msg: string, ctx?: Record<string, unknown>) => emit('info', msg, ctx),
  warn: (msg: string, ctx?: Record<string, unknown>) => emit('warn', msg, ctx),
  error: (msg: string, ctx?: Record<string, unknown>) => emit('error', msg, ctx),
}
